import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { HeartPulse, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, statusOf } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { Loader, NoAccess, PageHeader } from "@/components/common";
import { CareStrip } from "@/pages/CareProfile";
import { useLiveRefresh } from "@/lib/live";
import { writesLocked } from "@/lib/splits";
import { RecordList } from "@/components/health/Records";
import Growth from "@/components/health/Growth";
import Milestones from "@/components/health/Milestones";
import { Files, Sharing } from "@/components/health/Extras";

const RGB = "var(--glow-family)";
const GROUPS = ["appointments", "medications", "vaccines", "doctors", "conditions"];

function PageView({ hub, pageKey, presets, locked }) {
  const base = `/organizations/${hub.id}/health/${pageKey}`;
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("records");
  const load = useCallback(() => api.get(base).then(({ data }) => { setP(data); setErr(null); }).catch((e) => setErr(statusOf(e) === 403 ? "denied" : errMsg(e))), [base]);
  useEffect(() => { setP(null); load(); }, [load]);
  useLiveRefresh(load, (m) => m.entity === "health" && m.payload?.page === pageKey, 30000);
  if (p && p.key !== pageKey) return <Loader />;
  if (err === "denied") return <p className="q-card flex items-center gap-2 p-4 text-sm text-q-muted" data-testid="health-denied"><Lock className="h-4 w-4" /> This health page is private.</p>;
  if (!p) return <Loader />;
  const edit = p.mode === "edit", kidView = p.mode === "kid";
  const taken = (r, time) => api.post(`${base}/medications/${r.id}/taken`, { time }).then(() => { toast.success("Marked taken"); load(); }).catch((e) => toast.error(errMsg(e)));
  const tabs = [["records", "Health"], ...(p.kind === "kid" && p.growth ? [["growth", "Growth & milestones"]] : []), ...(edit ? [["files", "Files & insurance"], ["sharing", "Sharing"]] : [])];
  return (
    <div className="min-w-0 space-y-6" data-testid="health-page">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="min-w-0 flex-1 font-display text-2xl font-extrabold text-q-text" data-testid="health-page-name">{p.name}</h2>
        {p.mode === "view" && <span className="text-xs text-q-muted" data-testid="health-view-only">Shared with you · view only</span>}
      </div>
      {tabs.length > 1 && <div className="flex max-w-full gap-1 overflow-x-auto rounded-full border border-q-line p-1" role="tablist">{tabs.map(([k, l]) => (
        <button key={k} role="tab" aria-selected={tab === k} className={`min-h-[44px] shrink-0 whitespace-nowrap rounded-full px-4 text-sm font-semibold ${tab === k ? "bg-q-raised text-q-text" : "text-q-muted"}`} onClick={() => setTab(k)} data-testid={`health-tab-${k}`}>{l}</button>))}</div>}
      {tab === "records" && GROUPS.filter((g) => p[g]).map((g) => (
        <RecordList key={g} base={base} group={g} items={p[g]} canEdit={edit} locked={locked} kid={p.kind === "kid"} reload={load} onTaken={edit || kidView ? taken : null} />))}
      {tab === "growth" && <><Growth base={base} items={p.growth} canEdit={edit} locked={locked} reload={load} /><Milestones hubId={hub.id} base={base} items={p.milestones} presets={presets} tz={hub.timezone} canEdit={edit} locked={locked} reload={load} /></>}
      {tab === "files" && edit && <Files hubId={hub.id} base={base} page={p} locked={locked} reload={load} />}
      {tab === "sharing" && edit && <Sharing base={base} page={p} locked={locked} reload={load} />}
    </div>
  );
}

export default function Health() {
  const { hub } = useHub();
  const [sp, setSp] = useSearchParams();
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/health`).then(({ data }) => setD(data)).catch((e) => (statusOf(e) === 403 ? setDenied(true) : toast.error(errMsg(e)))), [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.entity === "health" && m.payload?.group === "sharing", 60000);
  if (denied) return <NoAccess />;
  if (!d) return <Loader />;
  const sel = sp.get("p") && d.pages.some((x) => x.key === sp.get("p")) ? sp.get("p") : (d.pages.find((x) => x.key === d.me) || d.pages[0])?.key;
  return (
    <div className="min-w-0" data-testid="health-hub">
      <PageHeader eyebrow="Family" title="Health" rgb={RGB} testid="health-title" />
      {hub.my_role !== "kid" && <CareStrip hub={hub} />}
      <div className="mb-5 flex flex-wrap gap-2" data-testid="health-people">{d.pages.map((x) => (
        <button key={x.key} className={`q-btn min-h-[44px] ${sel === x.key ? "q-btn-primary" : "q-btn-ghost"}`} onClick={() => setSp({ p: x.key })} data-testid={`health-person-${x.name.split(" ")[0]}`}>
          <HeartPulse className="h-4 w-4" /> {x.key === d.me ? "Me" : x.name.split(" ")[0]}{x.mode === "view" && <span className="text-[10px] text-q-faint">shared</span>}</button>))}</div>
      {sel ? <PageView key={sel} hub={hub} pageKey={sel} presets={d.presets} locked={writesLocked(hub)} /> : <p className="text-sm text-q-muted">No health pages shared with you.</p>}
    </div>
  );
}
